import PrecisionManufacturingIcon from "@mui/icons-material/PrecisionManufacturing";
import { Typography } from "@mui/material";
import classNames from "classnames";
import * as React from "react";

import { TopicNode } from "@/shared/domain/topics";

import {
  useEphemeralWorkspaceState,
  useTopicTreeForFile,
} from "../../WorkspaceCtx";
import { isFilterEmpty, type KeywordFilter } from "../filter";
import { TopicFilterEngine } from "../TopicFilterEngine";
import { buildTopicTree, TopicTreeNode } from "../TopicTree/buildTopicTree";
import { Topic } from "../TopicTree/Topic";

import styles from "./TopicTree.module.css";

interface FilteredTopicsProps {
  fileId: string | undefined;
  filter: KeywordFilter;
}

export function TopicTree(props: FilteredTopicsProps) {
  const { fileId, filter } = props;
  const topicNodes = useTopicTreeForFile(fileId);

  const { topics } = useEphemeralWorkspaceState();

  const topicFilterEngine = React.useMemo(() => {
    return new TopicFilterEngine(topics);
  }, [topics]);

  const results = React.useMemo(
    () => topicFilterEngine.filter(filter),
    [topicFilterEngine, filter],
  );

  const isQueryEmpty = isFilterEmpty(filter);
  const topicTree: TopicTreeNode<TopicNode>[] = React.useMemo(() => {
    // Representation of topic and message path nodes wrapped with UI state
    return buildTopicTree({
      topicNodes,
      filterResults: results,
      isQueryEmpty,
    });
  }, [isQueryEmpty, results, topicNodes]);

  const noResults = results.length === 0;

  return (
    <div className={classNames(styles.topicRoot)}>
      <NoMatchingTopicsMessage enabled={noResults && !isQueryEmpty} />
      <NoTopicsMessage enabled={noResults && isQueryEmpty} />
      {topicTree.map((node) => {
        return <Topic key={node.data.id} node={node} />;
      })}
    </div>
  );
}

interface NoTopicsMessageProps {
  enabled: boolean;
}

function NoMatchingTopicsMessage({ enabled }: NoTopicsMessageProps) {
  if (!enabled) {
    return null;
  }

  return (
    <div className={styles.noResultsContainer}>
      <PrecisionManufacturingIcon className={styles.noResultsIcon} />
      <Typography variant="h6" component={"h3"}>
        No results found
      </Typography>
      <Typography component={"p"} variant="body2">
        Try searching again
      </Typography>
    </div>
  );
}

function NoTopicsMessage({ enabled }: NoTopicsMessageProps) {
  if (!enabled) {
    return null;
  }

  return (
    <div className={styles.noResultsContainer}>
      <PrecisionManufacturingIcon className={styles.noResultsIcon} />
      <Typography variant="h6" component={"h3"}>
        No topics available
      </Typography>
    </div>
  );
}
